<template>
    <div class="video-input-container">
        <input ref="fileInput" class="file-input" type="file" @change="onFileChange" :accept="mimeTypes"/>
        <div v-ripple @click="pickFile" class="btn-container">
            <slot name="button">
                <v-btn>{{ $t('common.select_file') }}</v-btn>
            </slot>
        </div>
    </div>
</template>

<script>
import {storageRef} from '@/utils/Firebase'

export default {
    name: "FileUploader",
    props: {
        relativePath: {
            type: String,
            default: 'files'
        },
        mimeTypes: {
            type: String,
            default: 'video/*'
        },
        limitSize: {
            type: [String, Number],
            default: null,
        },
        maxDuration: {
            type: Number,
            default: null,
        },
        durationTolerance: {
            type: Number,
            default: 0,
        }
    },
    data() {
        return {
            progress: 0,
            file: null,
            megabyte: 1048576,
        }
    },
    methods: {
        reset() {
            const vm = this
            vm.file = null
            vm.$refs.fileInput.value = null
            vm.progress = 0
        },
        pickFile() {
            const vm = this
            vm.$refs.fileInput.click()
        },
        readVideoDuration(file) {
            return new Promise(resolve => {
                const url = URL.createObjectURL(file)
                const video = document.createElement('video')
                const done = value => {
                    URL.revokeObjectURL(url)
                    resolve(value)
                }
                video.preload = 'metadata'
                video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null)
                video.onerror = () => done(null)
                video.src = url
            })
        },
        captureVideoFrame(file) {
            return new Promise(resolve => {
                const url = URL.createObjectURL(file)
                const video = document.createElement('video')
                let finished = false
                const done = value => {
                    if (finished) return
                    finished = true
                    clearTimeout(timer)
                    video.removeAttribute('src')
                    video.load()
                    URL.revokeObjectURL(url)
                    resolve(value)
                }
                const timer = setTimeout(() => done(null), 5000)
                video.muted = true
                video.playsInline = true
                video.preload = 'auto'
                video.onloadeddata = () => {
                    video.currentTime = Math.min(0.5, (video.duration || 0) / 2)
                }
                video.onseeked = () => {
                    try {
                        const scale = Math.min(1, 480 / (video.videoWidth || 480))
                        const canvas = document.createElement('canvas')
                        canvas.width = Math.round((video.videoWidth || 480) * scale)
                        canvas.height = Math.round((video.videoHeight || 270) * scale)
                        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
                        canvas.toBlob(blob => done(blob ? URL.createObjectURL(blob) : null), 'image/jpeg', 0.8)
                    } catch (e) {
                        done(null)
                    }
                }
                video.onerror = () => done(null)
                video.src = url
            })
        },
        async onFileChange(evt) {
            const vm = this
            const file = evt.target.files[0]
            if (!file) return

            if (vm.limitSize && file.size > (vm.limitSize * vm.megabyte)) {
                vm.$refs.fileInput.value = null
                if (vm.$listeners.oversize) return vm.$emit('oversize', file)
                return vm.toast(vm.$t('common.limit_file_size', {size: Math.ceil(file.size / vm.megabyte), max: vm.limitSize}), 'error')
            }

            if (vm.maxDuration !== null && file.type.startsWith('video/')) {
                if (!vm.maxDuration) {
                    vm.$refs.fileInput.value = null
                    return vm.toast(vm.$t('common.video_not_allowed'), 'error')
                }
                const duration = await vm.readVideoDuration(file)
                if (duration !== null && duration >= vm.maxDuration + vm.durationTolerance) {
                    vm.$refs.fileInput.value = null
                    return vm.toast(vm.$t('common.limit_video_duration', {duration: Math.floor(duration * 10) / 10, max: vm.maxDuration}), 'error')
                }
            }

            vm.file = file
            vm.$emit('change', {
                name: vm.file.name,
                url: URL.createObjectURL(vm.file),
                thumb: file.type.startsWith('video/') ? await vm.captureVideoFrame(file) : null
            })
        },
        async upload() {
            const vm = this
            const file = vm.file
            if (!file) return console.log('no file selected')
            const savePath = `/${vm.currentUser.uid}/${vm.relativePath}/${vm.$moment().unix()}-${file.name}`
            let uploadTask = await storageRef.child(savePath).put(file)
            vm.$emit('success', {downloadURL: await uploadTask.ref.getDownloadURL(), savePath: savePath})
        },
    }
}
</script>

<style scoped>
.video-input-container {
    display: inline-block;
}

.btn-container {
    display: inline-block;
}

.file-input {
    display: none;
}
</style>